import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { preload, Config } from "@imgly/background-removal";

import { ERROR } from "types/snackBar.constant";
import { CSS_VARIABLES } from "enums/cssVariables";

import { useBackgroundRemovalWorker } from "workers/useBackgroundRemovalWorker";
import { useUser } from "components/helper/userContext";
import { useHandleErrors } from "components/authentificationMode/common/handleErrors";

import imageHelper from "components/helper/imageHelper";
import {
    DisplayedError,
    HTTPResponseError,
    NotFoundError,
    UnknownError,
} from "services/errors";

import Layout from "components/common/Layout";
import ErrorPage from "components/common/ErrorPage";
import Spinner from "components/common/Spinner";
import PhotoEditor from "components/photo/PhotoEditor";
import PhotoSelector from "components/photo/PhotoSelector";
import { sendPhoto } from "components/helper/callApi";

const MAX_IMG_SIZE = 1280;
const MAX_IMG_WEIGHT = 200;
const maxWidth = 400;
const config: Config = {
    model: "isnet_quint8",
    output: {
        format: "image/webp",
    },
};

const Photo = () => {
    const { setUser } = useUser();
    const { handleLoginError } = useHandleErrors();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [backgroundRemovedUrl, setBackgroundRemovedUrl] = useState<
        string | null
    >(null);
    const sourceImageRef = useRef<HTMLImageElement | null>(null);
    const finalImageRef = useRef<HTMLImageElement | null>(null);
    const [processing, setProcessing] = useState(false);
    const [canCancelled, setCanCancelled] = useState(false);
    const [error, setError] = useState<DisplayedError | null>(null);
    const { startWorker, cancelWorker } = useBackgroundRemovalWorker();

    const revokeBlobUrl = (url: string | null) => {
        if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    };

    useEffect(() => {
        return () => {
            revokeBlobUrl(previewUrl);
            revokeBlobUrl(backgroundRemovedUrl);
        };
    }, [previewUrl, backgroundRemovedUrl]);

    useEffect(() => {
        preload(config)
            .then(() => console.log("Asset preloading succeeded"))
            .catch(console.error);
    }, []);

    const handleTakeOrSelectPhoto = async (image: string) => {
        setProcessing(true);

        if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
        }

        try {
            sourceImageRef.current = await imageHelper.loadImage(image);
            finalImageRef.current = sourceImageRef.current;
            setPreviewUrl(image);
        } catch (err) {
            console.error("Unable to load the image:", err);
            enqueueSnackbar(t("photo.uploadError"), {
                variant: ERROR,
            });
        } finally {
            setProcessing(false);
        }
    };

    const transformImage = async (area: any): Promise<Blob | null> => {
        if (!finalImageRef.current || !area) return null;

        const blob = await imageHelper.processImage(
            finalImageRef.current,
            area,
            MAX_IMG_SIZE,
            MAX_IMG_WEIGHT,
        );

        return blob;
    };

    const handleRemoveBackground = async (area: any) => {
        if (!sourceImageRef.current || !area) return;

        setProcessing(true);
        setCanCancelled(true);

        try {
            startWorker({
                image: sourceImageRef.current,
                area,
                config,
                onSuccess: async (resultBlob) => {
                    const url = URL.createObjectURL(resultBlob);
                    setBackgroundRemovedUrl(url);
                    const newImage = await imageHelper.loadImage(url);
                    finalImageRef.current = newImage;
                    setProcessing(false);
                    setCanCancelled(false);
                },
                onError: (err) => {
                    console.error(err);
                    setProcessing(false);
                    setCanCancelled(false);
                },
            });
        } catch (err) {
            setProcessing(false);
            setCanCancelled(false);
        }
    };

    const handleSendPhoto = async (area: any) => {
        setProcessing(true);

        try {
            const blob = await transformImage(area);
            if (!blob) throw new Error("No image to send");

            const refreshedUser = await sendPhoto(blob);
            setUser(
                refreshedUser
                    ? { ...refreshedUser, isPhotoReceived: true }
                    : refreshedUser,
            );
        } catch (err) {
            if (err instanceof HTTPResponseError) {
                handleLoginError(err.response.status);
            } else {
                setError(err as NotFoundError | UnknownError);
            }
        } finally {
            setProcessing(false);
        }
    };

    if (error) {
        return <ErrorPage error={error} />;
    }

    if (processing) {
        return (
            <SpinnerWrapper>
                <SpinnerContent>
                    <Spinner />
                    {canCancelled && (
                        <CancelButton
                            onClick={() => {
                                cancelWorker();
                                setProcessing(false);
                                setCanCancelled(false);
                            }}
                        >
                            {t("cancel")}
                        </CancelButton>
                    )}
                </SpinnerContent>
            </SpinnerWrapper>
        );
    }

    return (
        <Layout>
            <Wrapper>
                {!previewUrl ? (
                    <PhotoSelector
                        onSelect={handleTakeOrSelectPhoto}
                        onSkip={() =>
                            setUser((u) =>
                                u ? { ...u, isPhotoReceived: true } : u,
                            )
                        }
                    />
                ) : (
                    <PhotoEditor
                        previewUrl={previewUrl}
                        backgroundRemovedUrl={backgroundRemovedUrl}
                        onRemoveBackground={(area) =>
                            handleRemoveBackground(area)
                        }
                        onResetRemoveBackground={() =>
                            setBackgroundRemovedUrl(null)
                        }
                        onClear={() => {
                            setPreviewUrl(null);
                            setBackgroundRemovedUrl(null);
                        }}
                        onValidate={handleSendPhoto}
                    />
                )}
            </Wrapper>
        </Layout>
    );
};

export default Photo;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: auto;
    max-width: ${maxWidth}px;
`;

const SpinnerWrapper = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    z-index: 2000;
`;

const SpinnerContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const CancelButton = styled.button`
    margin-top: 16px;
    border-radius: 8px;
    text-decoration: underline;
    background: #fff;
    border: 0px;
    cursor: pointer;
    color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
`;
